/**
 * IframeEventEmitter - класс для отправки структурированных сообщений из iframe в parent window
 * Поддерживает очередь сообщений, обработку ошибок и retry логику
 */

const DEFAULT_TIMEOUT = 5000; // 5 seconds
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

export class IframeEventEmitter {
  constructor(options = {}) {
    this.targetOrigin = options.targetOrigin || '*'; // По умолчанию отправляем везде (можно ограничить доменом)
    this.messageQueue = [];
    this.pendingMessages = new Map(); // messageId -> { resolve, reject, timeout }
    this.messageIdCounter = 0;
    this.enabled = false;
    this.debug = options.debug || false;

    // Проверяем, работаем ли мы внутри iframe
    this.checkIframeContext();
  }

  /**
   * Проверка контекста iframe
   */
  checkIframeContext() {
    try {
      this.enabled = window.self !== window.top;
      if (this.debug && this.enabled) {
        console.log('[IframeEventEmitter] Running inside iframe, emitter enabled');
      }
    } catch (e) {
      // Если выбросилось исключение, мы точно в iframe с другим origin
      this.enabled = true;
      if (this.debug) {
        console.log('[IframeEventEmitter] Cross-origin iframe detected, emitter enabled');
      }
    }
  }

  /**
   * Отправка события в parent window
   * @param {string} eventType - Тип события (например: 'iframe-ready', 'navigation-event')
   * @param {Object} data - Данные события
   * @param {Object} options - Опции (expectResponse, timeout)
   * @returns {Promise<Object|void>} Promise с ответом от parent (если expectResponse=true)
   */
  async emit(eventType, data = {}, options = {}) {
    if (!this.enabled) {
      if (this.debug) {
        console.warn('[IframeEventEmitter] Not in iframe context, skipping emit');
      }
      return;
    }

    const messageId = ++this.messageIdCounter;
    const message = {
      source: 'b2b-demo-iframe',
      messageId,
      eventType,
      timestamp: Date.now(),
      data,
    };

    if (this.debug) {
      console.log(`[IframeEventEmitter] Emitting event: ${eventType}`, message);
    }

    // Если ожидаем ответ, создаем Promise
    if (options.expectResponse) {
      return this._sendWithResponse(message, options.timeout || DEFAULT_TIMEOUT);
    }

    // Просто отправляем без ожидания ответа
    this._sendMessage(message);
  }

  /**
   * Отправка сообщения с ожиданием ответа
   * @private
   */
  _sendWithResponse(message, timeout) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingMessages.delete(message.messageId);
        reject(new Error(`Message ${message.messageId} timed out after ${timeout}ms`));
      }, timeout);

      this.pendingMessages.set(message.messageId, {
        resolve,
        reject,
        timeoutId,
      });

      this._sendMessage(message);
    });
  }

  /**
   * Отправка сообщения через postMessage
   * @private
   */
  _sendMessage(message) {
    try {
      if (!window.parent) {
        throw new Error('window.parent is not available');
      }

      window.parent.postMessage(message, this.targetOrigin);

      if (this.debug) {
        console.log(`[IframeEventEmitter] Message sent:`, message);
      }
    } catch (error) {
      console.error('[IframeEventEmitter] Failed to send message:', error);
      throw error;
    }
  }

  /**
   * Обработка ответа от parent window
   * @param {Object} response - Ответное сообщение
   */
  handleResponse(response) {
    const { messageId, success, data, error } = response;

    const pending = this.pendingMessages.get(messageId);
    if (!pending) {
      if (this.debug) {
        console.warn(`[IframeEventEmitter] Received response for unknown messageId: ${messageId}`);
      }
      return;
    }

    clearTimeout(pending.timeoutId);
    this.pendingMessages.delete(messageId);

    if (success) {
      pending.resolve(data);
    } else {
      pending.reject(new Error(error || 'Unknown error'));
    }
  }

  /**
   * Хелперы для отправки специфичных событий
   */

  /**
   * Сообщить parent что iframe готов к работе
   */
  emitReady() {
    return this.emit('iframe-ready', {
      timestamp: Date.now(),
      url: window.location.href,
    });
  }

  /**
   * Сообщить parent о навигации
   * @param {Object} navigationData - { route, carId, queryParams }
   */
  emitNavigation(navigationData) {
    return this.emit('navigation-event', navigationData);
  }

  /**
   * Сообщить parent о применении фильтров
   * @param {Object} filters - Примененные фильтры
   */
  emitFiltersApplied(filters) {
    return this.emit('filters-applied', { filters });
  }

  /**
   * Сообщить parent о текущем состоянии
   * @param {Object} state - Состояние приложения
   */
  emitStateResponse(state) {
    return this.emit('state-response', state);
  }

  /**
   * Сообщить parent об ошибке
   * @param {string} errorMessage - Сообщение об ошибке
   * @param {Object} context - Контекст ошибки
   */
  emitError(errorMessage, context = {}) {
    return this.emit('error', {
      message: errorMessage,
      context,
      timestamp: Date.now(),
    });
  }

  /**
   * Issue #577: Forward analytics event to parent window for GTM dataLayer.
   * Parent page should listen for 'analytics-event' and push to dataLayer.
   * @param {Object} event - GTM dataLayer event object (with `event` property)
   */
  emitAnalyticsEvent(event) {
    return this.emit('analytics-event', event);
  }

  /**
   * Очистка ресурсов
   */
  destroy() {
    // Отменяем все ожидающие сообщения
    for (const [messageId, pending] of this.pendingMessages.entries()) {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error('IframeEventEmitter destroyed'));
    }
    this.pendingMessages.clear();
    this.messageQueue = [];
    this.enabled = false;

    if (this.debug) {
      console.log('[IframeEventEmitter] Destroyed');
    }
  }
}

// Singleton instance для использования в приложении
let emitterInstance = null;

/**
 * Получить singleton instance эмиттера
 * @param {Object} options - Опции для инициализации (только при первом вызове)
 * @returns {IframeEventEmitter}
 */
export function getIframeEventEmitter(options) {
  if (!emitterInstance) {
    emitterInstance = new IframeEventEmitter(options);
  }
  return emitterInstance;
}

/**
 * Уничтожить singleton instance (для тестов)
 */
export function destroyIframeEventEmitter() {
  if (emitterInstance) {
    emitterInstance.destroy();
    emitterInstance = null;
  }
}
